import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { variantSummary } from "./variant-quantities.mjs";
import { rupiah } from "@/lib/format";
import receiptFontUrl from '@/assets/receipt-fonts/RobotoCondensed-Latin.woff2';
import { receiptHeader } from './receipt-header';
import { receiptGeometry, receiptBoxCss } from './receipt-geometry';
import { isNotaTemplate, notaStyles, notaReadyScript } from './nota-layout';
import { isCheckingReceipt, CHECKING_FOOTER } from './checking-receipt';
import { receiptGroups, nonTobaccoReceipt, compactReceiptReference } from './receipt-groups.mjs';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

const methodLabel = { tunai: "Tunai", transfer: "Transfer", qris: "QRIS" };
const amount = value => rupiah(value).replace(/^Rp\s*/, '');

// Use only the saved logo for this hostname, never a theme-editor preview or
// an arbitrary URL. Assets are tenant-scoped and served from our own origin.
function savedReceiptLogo() {
  try {
    const saved = JSON.parse(localStorage.getItem('masivi_appearance:v1:' + window.location.hostname));
    const asset = saved?.branding?.logo;
    return /^[a-f0-9]{32}\.png$/.test(asset) ? `/api/v1/appearance/assets/${asset}` : '';
  } catch { return ''; }
}

// Browser text weight is separate from Epson hardware heat/density settings.
export function buildReceiptHtml(tx, s = {}, cashier = "", { preview = false, comparison = false } = {}) {
  if (!tx) return '';
  const partial = tx.receipt_scope === 'non_tobacco';
  if (partial) tx = nonTobaccoReceipt(tx);
  const groups = receiptGroups(tx.items);
  tx = { ...tx, items: groups.items };
  const checking = isCheckingReceipt(tx);
  const customerAtTop = checking || s.receipt_template === 'nota1';
  const nota = checking || isNotaTemplate(s), nota3 = !checking && s.receipt_template === 'nota3';
  // Comparison is a screen-only presentation. Never alter a real print job or
  // the saved logo/header settings when opening the comparison dialog.
  const comparing = preview && comparison;
  const logo = checking || comparing || s.store_id ? '' : savedReceiptLogo();
  const header = receiptHeader(comparing ? { ...s, receipt_header_mode:'text' } : s);
  const { paper: paperWidth } = receiptGeometry(s);
  // A small black outline strengthens fine strokes without making every line bold.
  // This changes browser artwork only, not the printer's thermal heat setting.
  const textStroke = ({ normal: '0', dark: '0.26', extra: '0.36' })[s.receipt_ink] || '0.26';
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const line = '<div class="hr"></div>';
  const row = (l, r, b) => `<div class="row${b ? " b" : ""}"><span>${escapeHtml(l)}</span><span>${escapeHtml(r)}</span></div>`;
  // Share column widths across all items, including different quantity lengths.
  // Cap unusual labels/numbers so narrow paper wraps instead of clipping.
  const qtyChars = tx.items.reduce((n, it) => Math.max(n, Math.min(6, String(it.qty).length)), 1);
  const unitChars = tx.items.reduce((n, it) => Math.max(n, Math.min(8, (it.price_level || '').trim().length)), 4);
  const amountChars = tx.items.reduce((n, it) => Math.max(n, Math.min(14, amount(it.qty * it.price).length)), 1);
  const priceChars = tx.items.reduce((n, it) => Math.max(n, Math.min(14, amount(it.price).length)), 1);

  let items = "";
  tx.items.forEach((it, index) => {
    if (index === groups.separator) items += '<div class="hr receipt-category-separator"></div>';
    const lvl = (it.price_level || "").trim().toUpperCase();
    items += `<div class="it"><div class="item-main"><div class="item-name">${escapeHtml(it.name)}</div></div>` +
      `<div class="item-detail"><span class="item-calculation"><span class="item-qty">${escapeHtml(it.qty)}</span>${lvl ? ` <span class="item-unit-slot"><span class="item-unit">${escapeHtml(lvl)}</span></span>` : '<span class="item-unit-slot"></span>'} <span class="item-times">×</span> <span class="item-price">${escapeHtml(amount(it.price))}</span></span><span class="item-amount">${escapeHtml(amount(it.qty * it.price))}</span></div>`;
    if (variantSummary(it)) items += `<div class="it-variants">${escapeHtml(variantSummary(it))}</div>`;
    if (it.discount > 0) items += `<div class="row"><span>&nbsp;&nbsp;Diskon item</span><span>-${rupiah(it.discount)}</span></div>`;
    if (it.note) items += `<div class="it-note">* ${escapeHtml(it.note)}</div>`;
    items += `</div>`;
  });

  let pays = "";
  (tx.checkout_payments || tx.payments || []).forEach((p) => { pays += row(methodLabel[p.method] || p.method, rupiah(p.amount)); });

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Struk ${escapeHtml(tx.invoice_no)}</title>
    <style>
      /* Bundled font: receipt typography must not depend on fonts installed on the till. */
      @font-face { font-family: 'Roboto Condensed'; src: url('${receiptFontUrl}') format('woff2'); font-style: normal; font-weight: 100 900; font-display: block; }
      /* Driver chooses roll length. "80mm auto" is not a valid CSS page size. */
      @page { margin: 0; size: auto; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      /* Includes padding: never send a 78mm-wide document to a 72mm print head. */
      /* TES.jpeg showed first-column clipping at 2mm. Add left safety space
         inside the existing border-box; keep the right edge and font unchanged. */
      #r { ${receiptBoxCss(s)} font-family: 'Roboto Condensed', Arial, sans-serif; font-size: 12px; font-weight: 400; line-height: 1.35; font-variant-numeric: tabular-nums; font-variant-ligatures: none; font-feature-settings: 'tnum' 1, 'zero' 0; overflow-wrap: anywhere; }
      #r, #r * { color: #000 !important; -webkit-text-fill-color: #000; -webkit-text-stroke: ${textStroke}px #000; opacity: 1; text-shadow: none; }
      .c { text-align: center; }
      .b { font-weight: 700; }
      .receipt-logo { display: block; width: auto; max-width: 100%; max-height: 22mm; margin: 0 auto 6px; object-fit: contain; filter: grayscale(1) contrast(1.4); }
      .receipt-header-image { display: block; width: ${header.width}mm; max-width: 100%; margin: 0 auto 4px; }
      .big { font-family: 'Roboto Condensed', Arial, sans-serif; font-size: ${header.size}px; font-weight: 700; line-height: 1.1; font-feature-settings: 'tnum' 1, 'zero' 0; margin-bottom: 4px; overflow-wrap:anywhere; }
      .sm { font-size: inherit; }
      .hr { border-top: 1px dashed #000; margin: 4px 0; }
      .row, .total { display: flex; justify-content: space-between; gap: 8px; }
      .row { padding: 1px 0; }
      .row > span, .total > span { min-width: 0; }
      .row > span:last-child:not(:first-child), .total > span:last-child:not(:first-child) { flex-shrink: 0; max-width: 60%; text-align: right; }
      .sale-meta { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 35%); gap: 6px; margin-top: 8px; }
      .sale-meta > div { min-width:0; overflow-wrap:anywhere; }
      .cashier { text-align: right; }
      .invoice { margin-top: 4px; overflow-wrap:anywhere; text-align:center; }
      .invoice-party { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:2mm; align-items:start; }
      .invoice-party > span { min-width:0; white-space:pre-wrap; overflow-wrap:anywhere; }
      .invoice-party .invoice-number { text-align:left; }
      .invoice-party .invoice-customer { text-align:right; }
      .item-main { display: block; }
      .item-main > * { min-width: 0; }
      .item-amount, .item-price { text-align: right; }
      /* Receipt-wide columns keep × and both price columns aligned on every row. */
      .item-detail { padding-left: min(${paperWidth <= 58 ? 2 : 3}ch, 15%); margin-top: 2px; display: grid; grid-template-columns: minmax(0, 1fr) min(40%, ${amountChars}ch); column-gap: 6px; align-items: baseline; }
      .item-detail > span { min-width: 0; max-width: 100%; }
      .item-calculation { display: grid; grid-template-columns: minmax(0, ${qtyChars + 0.6}ch) minmax(0, ${unitChars * 1.25}ch) 1ch minmax(0, ${priceChars + 0.4}ch); column-gap: 0.4ch; align-items: baseline; justify-content: start; }
      .item-calculation > span { min-width: 0; }
      .item-qty { padding-right: 0.4ch; text-align: right; }
      .it { margin: 4px 0; break-inside: avoid; letter-spacing: 0.1px; }
      .item-name, .it-note { white-space: pre-wrap; overflow-wrap:anywhere; }
      .total { font-weight: 700; font-size: 14px; margin-top: 1px; }
      .row.b { font-size: 14px; }
      .item-count { margin-top: 8px; }
      .footer { margin-top: 8px; }
      .compact-item { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,30%); gap:8px; padding:1px 0; break-inside:avoid; }
      .compact-item > span { min-width:0; overflow-wrap:anywhere; }
      .compact-item > span:last-child { text-align:right; }
      .compact-heading, .compact-count { display:flex; justify-content:space-between; gap:8px; font-weight:700; }
      .compact-heading { margin-bottom:1px; }
      .compact-detail { white-space:pre-wrap; overflow-wrap:anywhere; padding-left:2mm; }
      .compact-note { white-space:pre-wrap; overflow-wrap:anywhere; margin-top:1mm; }
      ${nota ? notaStyles(checking ? 'nota1' : s.receipt_template, qtyChars, unitChars) : ''}
      ${checking ? `.checking-title { font-size:${paperWidth <= 58 ? 22 : 28}px; font-weight:700; line-height:1.1; margin-bottom:2mm; text-align:center; }
      .checking-subtitle { font-size:13px; text-align:center; margin-bottom:1mm; }
      .checking-store { font-size:15px; font-weight:400; text-align:center; }
      .checking-meta { font-size:12px; line-height:1.3; }
      .item-name { font-weight:400; }
      .total { font-size:20px; }
      .checking-inspector { margin-top:2mm; }
      .checking-footer { margin-top:3mm; font-size:${paperWidth <= 58 ? 13 : 14}px; line-height:1.2; font-weight:700; text-align:center; white-space:pre-line; }
      .checking-footer, .checking-title { break-inside:avoid; }` : ''}
      ${comparing ? '#r { margin:0 auto; padding-left:3mm; padding-right:3mm; }' : ''}
    </style></head><body><div id="r">
      ${partial ? `<div class="compact-receipt">${tx.customer_name ? `<div>Pelanggan: ${escapeHtml(tx.customer_name)}</div>` : ''}<div>${tx.queue_number || tx.invoice_no === 'DRAFT' ? 'Antrean' : 'No'}: ${escapeHtml(compactReceiptReference(tx))}</div>${line}<div class="compact-heading"><span>Produk</span><span>QTY</span></div>${tx.items.map(it => `<div class="compact-product"><div class="compact-item"><span>${escapeHtml(it.name)}</span><span>${escapeHtml(it.qty)} ${escapeHtml(it.unit || it.price_level || 'PCS')}</span></div>${variantSummary(it) ? `<div class="compact-detail compact-variants">${escapeHtml(variantSummary(it))}</div>` : ''}${it.note ? `<div class="compact-detail">Catatan: ${escapeHtml(it.note)}</div>` : ''}</div>`).join('')}${line}<div class="compact-count"><span>Total Item</span><span>${tx.items.length}</span></div>${tx.note ? `<div class="compact-note">Catatan: ${escapeHtml(tx.note)}</div>` : ''}</div>` : `
      ${nota ? '<div class="receipt-heading">' : ''}
      ${checking ? `<div class="checking-title">BUKAN BUKTI<br>PEMBAYARAN</div><div class="checking-subtitle">NOTA PENGECEKAN BARANG</div><div class="checking-store">${escapeHtml(s.store_name || DEFAULT_APPEARANCE.branding.name)}</div>` : header.mode === 'image' ? `<img class="receipt-header-image" src="${escapeHtml(header.image)}" alt="">` : header.mode === 'text' ? `${logo ? `<img class="receipt-logo" src="${escapeHtml(logo)}" alt="">` : ''}<div class="c big">${escapeHtml(s.store_name || DEFAULT_APPEARANCE.branding.name)}</div>` : ''}
      ${!nota && tx.is_reprint ? '<div class="c reprint">(REPRINT)</div>' : ''}
      ${!checking && s.address ? `<div class="c sm address">${escapeHtml(s.address)}</div>` : ""}
      ${!checking && s.phone ? `<div class="c sm phone">Telp ${escapeHtml(s.phone)}</div>` : ""}
      ${nota && tx.is_reprint ? '<div class="c reprint">(REPRINT)</div>' : ''}
      ${nota ? '</div>' : ''}
      ${nota3 ? line : ''}
      ${checking ? `${line}<div class="checking-meta">${tx.queue_number ? `No. Antrean: ${escapeHtml(tx.queue_number)}<br>` : 'Status: Draf belum dikirim<br>'}Tanggal: ${dateStr}<br>Kasir input: ${escapeHtml(cashier || tx.cashier)}${tx.customer_name ? `<br>Pelanggan: ${escapeHtml(tx.customer_name)}` : ''}</div>` : `<div class="sale-meta"><div>Waktu Penjualan<br>${dateStr}</div><div class="cashier">Kasir<br>${escapeHtml(cashier)}</div></div>${nota ? line : ''}${customerAtTop ? `<div class="invoice invoice-party"><span class="invoice-number">#${escapeHtml(tx.invoice_no)}</span><span class="invoice-customer">${escapeHtml(tx.customer_name)}</span></div>` : `<div class="invoice">#${escapeHtml(tx.invoice_no)}</div>`}`}
      ${line}
      ${nota ? '' : row("Item", "Jumlah") + line}
      <div class="items">${items}</div>
      ${line}
      ${row("Subtotal", amount(tx.subtotal))}
      ${tx.discount > 0 ? row("Diskon", "-" + rupiah(tx.discount)) : ""}
      ${(tx.previous_debts || []).map(debt => row("Piutang sebelumnya (" + debt.invoice_no.slice(-4) + ")", rupiah(debt.amount))).join("")}
      ${nota3 ? line : ''}
      <div class="total"><span>TOTAL</span><span>${amount(tx.total + (tx.settlement_total || 0))}</span></div>
      ${tx.fee > 0 ? row("Biaya", rupiah(tx.fee)) + row("TOTAL BAYAR", rupiah(tx.grand_total), true) : ""}
      ${tx.return_credit_applied > 0 ? row("Saldo Retur", "-" + rupiah(tx.return_credit_applied)) : ""}
      ${nota ? line : ''}
      ${checking ? '' : pays}
      ${checking ? '' : row("Bayar", rupiah(tx.paid))}
      ${checking ? '' : row("Kembali", rupiah(tx.change))}
      ${tx.customer_name && !customerAtTop ? row("Pelanggan", tx.customer_name) : ""}
      ${tx.credit > 0 ? row("SISA PIUTANG", rupiah(tx.credit)) : ""}
      ${nota ? line + `<div class="item-count"><span>Jumlah Produk</span><span>: ${tx.items.length}</span><span>Total QTY</span><span>: ${escapeHtml(Number(tx.items.reduce((sum, item) => sum + Number(item.qty || 0), 0).toFixed(6)))}</span></div>` : `<div class="item-count">Jumlah Item: ${tx.items.length}</div>`}
      ${tx.note ? `<div class="sm" style="white-space:pre-wrap;overflow-wrap:anywhere">Catatan: ${escapeHtml(tx.note)}</div>${line}` : ""}
      ${checking ? `${line}<div class="checking-inspector">Diperiksa oleh: __________________</div><div class="checking-footer">${escapeHtml(CHECKING_FOOTER)}</div>` : `<div class="c sm footer" style="white-space:pre-line">${escapeHtml(s.footer || "*** TERIMA KASIH ***")}</div>`}
      `}
    </div>
    ${nota ? notaReadyScript(preview, checking || nota3 ? 20 : 16) : `<script>window.onload=async function(){if(document.fonts){try{await Promise.all([document.fonts.load('400 12px "Roboto Condensed"'),document.fonts.load('700 14px "Roboto Condensed"'),document.fonts.load('700 ${header.size}px "Roboto Condensed"')]);await document.fonts.ready;}catch(e){}}await Promise.all(Array.from(document.images||[]).map(async function(img){try{if(img.decode)await img.decode();if(!img.naturalWidth)img.style.display="none";}catch(e){img.style.display="none";}}));${preview ? "document.documentElement.dataset.receiptReady='true';" : 'setTimeout(function(){try{window.focus();window.print();}catch(e){if(window.frameElement && window.frameElement.onReceiptPrintError)window.frameElement.onReceiptPrintError();}},150);'} };${preview ? '' : 'window.onafterprint=function(){if(window.frameElement && window.frameElement.onReceiptPrintDone)window.frameElement.onReceiptPrintDone();else window.close();};'}</script>`}
    </body></html>`;

  return html;
}

export function printReceipt(tx, s = {}, cashier = "") {
  if (!tx) return false;
  return printDocument(buildReceiptHtml(tx, s, cashier));
}

// Checkout finishes after an API request, outside the browser's click gesture.
// A same-origin print frame avoids blocked popups. Silent printing still requires
// the installed Windows shortcut's --kiosk-printing flag; normal Chrome shows a dialog.
export function printReceiptAutomatically(tx, s = {}, cashier = "", onError = () => {}) {
  if (!tx) return false;
  const frame = document.createElement('iframe');
  frame.title = 'Cetak struk otomatis';
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.dataset.receiptAutoPrint = 'true';
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:360px;height:640px;border:0;';
  let timer;
  const cleanup = () => { window.clearTimeout(timer); frame.remove(); };
  frame.onReceiptPrintDone = cleanup;
  frame.onReceiptPrintError = () => { cleanup(); onError(); };
  frame.onerror = frame.onReceiptPrintError;
  try {
    frame.srcdoc = buildReceiptHtml(tx, s, cashier);
    document.body.appendChild(frame);
    // Fallback for browsers that do not dispatch afterprint (including cancelled jobs).
    timer = window.setTimeout(cleanup, 10 * 60 * 1000);
    return true;
  } catch (error) {
    cleanup();
    return false;
  }
}

// Uses the actual receipt template, with synthetic data only. No API or stock writes.
export function buildReceiptFontTestHtml(s = {}) {
  const sample = {
    invoice_no: 'TES-CETAK', created_at: new Date().toISOString(),
    items: [
      { name: 'DJARUM SUPER 12', price_level: 'BKS', qty: 2, price: 24500, discount: 0 },
      { name: 'GG FILTER 12', price_level: 'SLOP', qty: 1, price: 235000, discount: 0 },
      { name: 'AQUA 600 ML', price_level: 'DUS', qty: 2, price: 48000, discount: 0 },
      { name: 'INDOMIE GORENG', price_level: 'PCS', qty: 5, price: 3500, discount: 0 },
      { name: 'KOPI ABC SUSU', price_level: 'RCG', qty: 3, price: 18500, discount: 0 },
    ],
    subtotal: 453000, total: 453000, paid: 453000, change: 0,
    payments: [{ method: 'tunai', amount: 453000 }],
  };
  return buildReceiptHtml(sample, { ...s, footer: 'Angka: 0123456789\nTES CETAK — BUKAN TRANSAKSI' }, 'KASIR 1');
}

export function printReceiptFontTest(s = {}) {
  return printDocument(buildReceiptFontTestHtml(s));
}

function printDocument(html) {
  const w = window.open("", "_blank", "width=360,height=640");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
